/**
 * `table` template — a live ranked SKU table over a cohort, formatted with the same
 * money/velocity formatters the field and capacity screens use.
 */

import { fmtMoneyShort, fmtVel } from "@/features/sku-velocity/field/util";
import type { TableView } from "@/lib/api";
import type { Sku } from "@/lib/data";
import { evalCohort } from "@/lib/specs/evaluate";

import type { ViewChrome, ViewSize } from "./view-shell";

type Column = NonNullable<TableView["columns"]>[number];

const COLUMN_LABEL: Record<Column, string> = {
  name: "SKU",
  brand: "Brand",
  size: "Size",
  type: "Type",
  band: "Band",
  price: "Price",
  vel: "Vel/mo",
  inv: "Inventory",
  value: "NTS £/yr",
  faces: "Faces",
  lastMovedQ: "Last moved",
  accounts: "Accounts",
  pickReco: "Pick reco",
  launched: "Launched",
};

function cellValue(sku: Sku, col: Column, currency?: string): string {
  switch (col) {
    case "name":
      return sku.name;
    case "brand":
      return sku.brand;
    case "size":
      return sku.size;
    case "type":
      return sku.type;
    case "band":
      return sku.band;
    case "price":
      return fmtMoneyShort(sku.price, currency);
    case "vel":
      return fmtVel(sku.vel);
    case "inv":
      return sku.inv.toLocaleString();
    case "value":
      return fmtMoneyShort(sku.value, currency);
    case "faces":
      return sku.faces.toLocaleString();
    case "lastMovedQ":
      return String(sku.lastMovedQ);
    case "accounts":
      return sku.accounts.toLocaleString();
    case "pickReco":
      return sku.pickReco;
    case "launched":
      return sku.launched;
  }
}

interface TableChartProps {
  spec: TableView;
  size: ViewSize;
  skus: readonly Sku[];
  currency?: string;
  chrome?: ViewChrome;
}

export function TableChart({ spec, size, skus, currency, chrome = "full" }: TableChartProps) {
  const cohortSkus = evalCohort(spec.cohort, skus);
  const sortBy = spec.sort_by ?? "value";
  const descending = spec.descending ?? true;
  const sorted = [...cohortSkus].sort((a, b) =>
    descending ? b[sortBy] - a[sortBy] : a[sortBy] - b[sortBy],
  );
  const limit = spec.limit ?? 15;
  const rows = sorted.slice(0, limit);
  const columns = spec.columns ?? ["name", "type", "vel", "inv", "value", "faces"];

  if (!rows.length) {
    return <p className="text-muted-foreground text-xs">No SKUs in this cohort.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className={size === "card" ? "w-full text-[11px]" : "w-full text-sm"}>
        <thead>
          <tr className="border-border border-b text-left">
            {columns.map((col) => (
              <th
                key={col}
                className="text-muted-foreground px-1.5 py-1 font-medium whitespace-nowrap"
              >
                {COLUMN_LABEL[col]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((sku) => (
            <tr key={sku.id} className="border-border/60 border-b last:border-0">
              {columns.map((col) => (
                <td key={col} className="px-1.5 py-1 whitespace-nowrap">
                  {cellValue(sku, col, currency)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {chrome !== "bare" && cohortSkus.length > rows.length ? (
        <p className="text-muted-foreground mt-1 text-[11px]">
          Top {rows.length.toLocaleString()} of {cohortSkus.length.toLocaleString()}
        </p>
      ) : null}
    </div>
  );
}
